import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-core';
import React, { useEffect, useState } from 'react';
import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import { ContentWorkflow, WorkflowEventTypes } from '@amzn/awscat-pattern-library-service-model';
import FlashbarMessageBroker from '../../common/FlashbarMessageBroker';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import constants from '../../common/constants';

interface Props {
  workflow: ContentWorkflow;
  onAccept: () => void;
  onDecline: () => void;
  handleActionError: () => void;
}

let flashbarMessageBroker: FlashbarMessageBroker;
let mplServiceClient: IMplServiceClient;

const ReviewRequestActionButtons: React.FunctionComponent<Props> = ({
  workflow,
  onAccept,
  onDecline,
  handleActionError,
}: Props) => {
  const [showAcceptModal, setShowAcceptModal] = useState<boolean>(false);
  const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const [isDeclining, setIsDeclining] = useState<boolean>(false);

  useEffect(() => {
    flashbarMessageBroker = FlashbarMessageBroker.create();
    mplServiceClient = MplServiceClientFactory.create();
  }, []);

  const handleShowAcceptModal = () => setShowAcceptModal(true);
  const handleShowDeclineModal = () => setShowDeclineModal(true);
  const dismissAcceptModal = () => setShowAcceptModal(false);
  const dismissDeclineModal = () => setShowDeclineModal(false);

  const handleError = (error: any) => {
    if (error?.response?.status === 400 || error?.response?.status === 403) {
      flashbarMessageBroker.pushWarnMessage(constants.REVIEW_REQUEST_ALREADY_DECLINED_TEXT);
      handleActionError();
    } else {
      throw error;
    }
  };

  const onAcceptReviewRequest = async () => {
    setIsAccepting(true);
    try {
      await createWorkflowEventWithStatePolling(
        workflow.contentId,
        workflow.draftWorkflowId,
        WorkflowEventTypes.ACCEPT_REVIEW,
        '',
      );
      flashbarMessageBroker.pushSuccessMessage(constants.REVIEW_REQUEST_ACCEPTED_TEXT);
      onAccept();
    } catch (error) {
      handleError(error);
    } finally {
      setIsAccepting(false);
      dismissAcceptModal();
    }
  };

  const declineReviewRequest = async () => {
    await createWorkflowEventWithStatePolling(
      workflow.contentId,
      workflow.draftWorkflowId,
      WorkflowEventTypes.DECLINE_REVIEW,
      '',
    );
    flashbarMessageBroker.pushSuccessMessage(constants.REVIEW_REQUEST_DECLIEND_TEXT);
    onDecline();
  };

  const onDeclineReviewRequest = async () => {
    setIsDeclining(true);
    try {
      await declineReviewRequest();
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeclining(false);
      dismissDeclineModal();
    }
  };

  const acceptModalFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={dismissAcceptModal}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onAcceptReviewRequest} loading={isAccepting}>
          Confirm accept
        </Button>
      </SpaceBetween>
    </Box>
  );

  const acceptModal = (
    <Modal
      onDismiss={dismissAcceptModal}
      visible={showAcceptModal}
      footer={acceptModalFooter}
      header={'Accept review request'}
      closeAriaLabel="Close modal"
    >
      {constants.REVIEW_REQUEST_ACCEPT_MODAL_TEXT}
    </Modal>
  );

  const declineModalFooter = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="link" onClick={dismissDeclineModal}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onDeclineReviewRequest} loading={isDeclining}>
          Confirm decline
        </Button>
      </SpaceBetween>
    </Box>
  );

  const declineModal = (
    <Modal
      onDismiss={dismissDeclineModal}
      visible={showDeclineModal}
      footer={declineModalFooter}
      header={'Decline review request'}
      closeAriaLabel="Close modal"
    >
      {constants.REVIEW_REQUEST_DECLINE_MODAL_TEXT}
    </Modal>
  );

  const actionButtons = (
    <Box float="right">
      <SpaceBetween direction="horizontal" size="xs">
        <Button onClick={handleShowAcceptModal}>Accept</Button>
        <Button onClick={handleShowDeclineModal}>Decline</Button>
      </SpaceBetween>
    </Box>
  );

  return (
    <>
      {actionButtons}
      {acceptModal}
      {declineModal}
    </>
  );
};

export default ReviewRequestActionButtons;
